import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';
import { CampaignReportData } from '../../../types/campaignReport';
import { formatPercent, formatNumberCompact, formatCurrencyGBP } from '../../../lib/reportFormatters';
import { Lightbulb } from 'lucide-react';

interface OverviewTabProps {
  data: CampaignReportData;
}

export function OverviewTab({ data }: OverviewTabProps) {
  return (
    <div className="space-y-8">
      {/* Campaign Performance Summary Table */}
      <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
        <CardHeader>
          <CardTitle className="text-xl font-bold" style={{ color: '#183df7' }}>
            Campaign Performance Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-semibold">Metric</TableHead>
                <TableHead className="text-right font-semibold">Value</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium">Footfall (6-month total)</TableCell>
                <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                  {formatNumberCompact(data.kpis.footfall.sixMonthTotal)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Screen Impressions (6-month total)</TableCell>
                <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                  {formatNumberCompact(data.kpis.screenImpressions.sixMonthTotal)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Total Units Sold</TableCell>
                <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                  {formatNumberCompact(data.kpis.totalUnitsSold)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Browse → Purchase Conversion</TableCell>
                <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                  {formatPercent(data.conversions.browseToPurchaseRate)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Footfall → Purchase Conversion</TableCell>
                <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                  {formatPercent(data.conversions.footfallToPurchaseRate)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Total Campaign Revenue</TableCell>
                <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                  {formatCurrencyGBP(data.revenue.totalCampaignRevenue)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Scorecard Table */}
      <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
        <CardHeader>
          <CardTitle className="text-xl font-bold" style={{ color: '#183df7' }}>
            Scorecard
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-semibold">Metric</TableHead>
                <TableHead className="text-right font-semibold">Score</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.kpis.footfallToBrowseRate && (
                <TableRow>
                  <TableCell className="font-medium">Footfall → Browse Rate</TableCell>
                  <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                    {formatPercent(data.kpis.footfallToBrowseRate)}
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell className="font-medium">Browse → Purchase Conversion</TableCell>
                <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                  {formatPercent(data.conversions.browseToPurchaseRate)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Footfall → Purchase Conversion</TableCell>
                <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                  {formatPercent(data.conversions.footfallToPurchaseRate)}
                </TableCell>
              </TableRow>
              {data.kpis.averageBrowseTime && (
                <TableRow>
                  <TableCell className="font-medium">Average Browse Time</TableCell>
                  <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                    {data.kpis.averageBrowseTime}s
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell className="font-medium">Experience Rating</TableCell>
                <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                  {data.feedback.overallExperienceScore} / 5
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Would Recommend</TableCell>
                <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                  {formatPercent(data.feedback.wouldRecommend)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Strategic Takeaways */}
      {data.strategicTakeaways && data.strategicTakeaways.points.length > 0 && (
        <Alert 
          className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100/50" 
          style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}
        >
          <Lightbulb className="h-5 w-5" style={{ color: '#183df7' }} />
          <AlertTitle className="text-xl font-bold" style={{ color: '#183df7' }}>
            🎯 Strategic Takeaways
          </AlertTitle>
          <AlertDescription className="mt-4">
            <ul className="space-y-3">
              {data.strategicTakeaways.points.map((point, index) => (
                <li key={index} className="text-sm text-gray-700 flex items-start gap-3">
                  <span className="font-bold text-lg" style={{ color: '#183df7' }}>•</span>
                  <span className="leading-relaxed">{point}</span>
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
